import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import './DeviceFrame.css';

/**
 * A hardware-style holder for a real Tulink product screenshot, angled slightly
 * so the screen reads as a device rather than a flat card.
 *
 * Nothing is drawn over the screen itself — the screenshots stay untouched.
 * With `video`, the screen plays a short silent loop over the `src` screenshot.
 * The screenshot stays underneath and the video only fades in once frames are
 * actually playing, so a browser that blocks autoplay still shows a screen
 * rather than a black rectangle. Reduced motion keeps the still screenshot.
 * The loop only plays while it is on screen.
 */
/** Older browsers return nothing from play(); newer ones reject when blocked. */
const tryPlay = (node) => {
  try {
    const result = node.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  } catch {
    // Playback is optional; the screenshot underneath stays visible.
  }
};

export const DeviceFrame = ({ src, video, alt, width, height, className = '' }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const playVideo = Boolean(video) && !prefersReducedMotion;
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return undefined;

    // React sets `muted` as a property only; autoplay policies read the attribute.
    node.muted = true;
    node.setAttribute('muted', '');

    if (typeof IntersectionObserver === 'undefined') {
      tryPlay(node);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        tryPlay(node);
      } else {
        node.pause();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [playVideo, video]);

  return (
    <div className={`tl-device ${className}`.trim()}>
      <div className="tl-device__shell">
        <span className="tl-device__button tl-device__button--volume" aria-hidden="true" />
        <span className="tl-device__button tl-device__button--power" aria-hidden="true" />
        <div className="tl-device__screen">
          <img
            className="tl-device__image"
            src={src}
            alt={playVideo ? '' : alt}
            aria-hidden={playVideo ? 'true' : undefined}
            width={width}
            height={height}
            loading="lazy"
            decoding="async"
          />
          {playVideo && (
            <video
              ref={videoRef}
              className={`tl-device__video${playing ? ' is-playing' : ''}`}
              src={video}
              width={width}
              height={height}
              aria-label={alt}
              role="img"
              autoPlay
              muted
              loop
              playsInline
              disablePictureInPicture
              preload="auto"
              onPlaying={() => setPlaying(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
